// 任务看板管理 API
import { createAppRequest } from '@/services/request';

const request = createAppRequest();

import type {
  CreateTaskBoardDto,
  CreateTaskBoardPanelDto,
  QueryTaskBoardDto,
  Task,
  TaskBoard,
  TaskBoardPanelEntity,
  TaskBoardStats,
  TaskBoardWithTasks,
  UpdateTaskBoardDto,
  UpdateTaskBoardPanelDto,
} from './type';

export interface TaskBoardPanelDetail extends TaskBoardPanelEntity {
  stats: TaskBoardStats;
}

// 任务看板管理 API
export const taskBoardApi = {
  createTaskBoard: (data: CreateTaskBoardDto) =>
    request<TaskBoard>('/bff/task-boards', {
      method: 'POST',
      data,
    }),

  // 获取任务看板列表
  getTaskBoards: (params?: QueryTaskBoardDto) => {
    const searchParams = new URLSearchParams();
    if (params?.page) {
      searchParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.keyword) {
      searchParams.append('keyword', params.keyword);
    }
    if (params?.type) {
      searchParams.append('type', params.type);
    }
    if (params?.isDefault !== undefined) {
      searchParams.append('isDefault', params.isDefault.toString());
    }
    if (params?.isPublic !== undefined) {
      searchParams.append('isPublic', params.isPublic.toString());
    }
    if (params?.creatorMemberUuid) {
      searchParams.append('creatorMemberUuid', params.creatorMemberUuid);
    }
    if (params?.sortBy) {
      searchParams.append('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      searchParams.append('sortOrder', params.sortOrder);
    }

    return request<{ taskBoards: TaskBoard[]; total: number }>(
      `/bff/task-boards?${searchParams.toString()}`,
      {
        method: 'GET',
      },
    );
  },

  getMyTaskBoards: () =>
    request<TaskBoard[]>('/bff/task-boards/my', {
      method: 'GET',
    }),

  getTaskBoardById: (uuid: string) =>
    request<TaskBoard>(`/bff/task-boards/${uuid}`, {
      method: 'GET',
    }),

  updateTaskBoard: (uuid: string, data: UpdateTaskBoardDto) =>
    request<TaskBoard>(`/bff/task-boards/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  deleteTaskBoard: (uuid: string) =>
    request<void>(`/bff/task-boards/${uuid}`, {
      method: 'DELETE',
    }),

  getTaskBoardsWithTasks: (limit: number = 5) =>
    request<TaskBoardWithTasks[]>(
      `/bff/task-boards/with-tasks?limit=${limit}`,
      {
        method: 'GET',
      },
    ),

  getTaskBoardDetail: (uuid: string) =>
    request<TaskBoard>(`/bff/task-boards/with-tasks/${uuid}`, {
      method: 'GET',
    }),

  // 获取指定看板的某个面板的任务列表
  getPanelTasks: (
    boardUuid: string,
    panelUuid: string,
    page: number = 1,
    limit: number = 50,
  ) =>
    request<{ tasks: Task[] } | Task[]>(
      `/bff/task-boards/with-tasks/${boardUuid}/${panelUuid}?page=${page}&limit=${limit}`,
      {
        method: 'GET',
      },
    ),

  // 获取指定看板的某个面板详情（包含任务统计）
  getPanelDetail: (boardUuid: string, panelUuid: string) =>
    request<TaskBoardPanelDetail>(
      `/bff/task-boards/${boardUuid}/panels/${panelUuid}`,
      {
        method: 'GET',
      },
    ),
};

// ============ 任务看板面板管理 API ============

// 看板面板管理 API
export const taskBoardPanelApi = {
  createPanel: (taskBoardUuid: string, data: CreateTaskBoardPanelDto) =>
    request<TaskBoardPanelEntity>(`/bff/task-boards/${taskBoardUuid}/panels`, {
      method: 'POST',
      data,
    }),

  getPanels: (taskBoardUuid: string) =>
    request<TaskBoardPanelEntity[]>(
      `/bff/task-boards/${taskBoardUuid}/panels`,
      {
        method: 'GET',
      },
    ),

  updatePanel: (panelUuid: string, data: UpdateTaskBoardPanelDto) =>
    request<TaskBoardPanelEntity>(`/bff/task-boards/panels/${panelUuid}`, {
      method: 'PATCH',
      data,
    }),

  deletePanel: (panelUuid: string) =>
    request<void>(`/bff/task-boards/panels/${panelUuid}`, {
      method: 'DELETE',
    }),
};
